<script type="text/jsx">
import { mapGetters, mapActions, mapMutations } from 'vuex';
import { BILLING_CYCLE, planTypes, BASE_PLAN_TYPES, DEFAULT_FREE_TRIAL_DAYS, BillingCycleEnum } from '@/common/constants';
import { routePathMap } from '@/router/constant';
import { get } from 'lodash';
import { dateTimeFormat, dateTimeFormatWithUtcOffset } from '@/utils/dateformat';
import { getDefaultPayApmInfo } from "@/store/modules/payment";
import { plansof } from '@/utils/chain';
import { pageConfig } from '@/common/service/page-config';
import { getPlanTypeByCategory } from '@/common/service/parse';
import { PLAN_TYPES_WITH_MULTI_BASE_PLANS } from '@/plans/ROOM_CONNECTOR/common/utils';
import { isEmptyVal } from '@/utils';
import { getMessageTemplateByRules } from '@/views/config/partner-refer/helper/validator';
import { getMultipleRatePlanSumPrice } from '@/store/modules/shopping-cart';
import { PLAN_PRICE_CHANGE_UTILS } from '@/common/priceChange';

// render priority list
export const renderKeys = ['renderPrice', 'renderTip', 'renderAgreement', 'renderSuccessTitle'];

// render controls
export const props = renderKeys.reduce((acc, x) => {
  acc[x] = {
    type: Boolean,
    default: false,
  };
  return acc;
}, {});

export const CART_DISPLAY_MODE = {
  BOUGHT: 'bought', // only bought
  DELTA: 'delta', // only delta added
  SUM: 'sum', // both bought and added
};

export default {
  props: {
    ...props,
    $pkg: Object, // plan config package of @/plans
    mode: {
      type: String,
      default: CART_DISPLAY_MODE.BOUGHT,
    },
    filterKeys: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    ...mapGetters(['getSelectedPlans', 'discountWithAmountMap', 'boughtPlans', 'apmInfo', 'partnerRefer', 'eligiblePartnerReferOffer', 'migratePlanState']),
    mx_plans() {
      const key = this.$options.key;
      const type = planTypes[key];
      return this.getSelectedPlans.filter((x) => x.type === type);
    },
    mx_startDate() {
      // TODO consider paid2paid date
      return new Date();
    },
    mx_isLastBasePlan() {
      // is this the last base plan that should not be removed
      const key = this.$options.key;
      const type = planTypes[key];
      if (!BASE_PLAN_TYPES.includes(type)) return false; // is not base plan
      let baseTypes = this.mx_plans.map((x) => x.type).filter((x) => BASE_PLAN_TYPES.includes(x)); // base plans among these
      if (baseTypes.length === 1 && baseTypes[0] === type) return true; // only when

      return false;
    },
    // For CRC special case that need to check the last same billing cycle base plan since it could be more than 1 base plan
    mx_isLastBasePlanForTheSameBillingCycle() {
      const { from: currentPrimaryPlan } = pageConfig;
      // Some plan may has more than 1 base plan dependencies like CRC
      const hasThePlanNeedsMultiBasePlan = PLAN_TYPES_WITH_MULTI_BASE_PLANS.includes(currentPrimaryPlan);
      // Get current mixin base plan type like zoom_meeting zoomroom
      const key = this.$options.key;
      const type = planTypes[key];
      // e.g CRC as the primary plan
      if(hasThePlanNeedsMultiBasePlan) {
        // CRC billing cycle
        const currentPrimaryPlanBillingCycle = plansof(this.getSelectedPlans).type(getPlanTypeByCategory(currentPrimaryPlan)).get(0).billing_cycle;
        // Filter base plans which are the same billing cycle or annual > monthly with CRC
        const SameBillingCycleBasePlans = plansof(this.getSelectedPlans).type(...BASE_PLAN_TYPES).filter(basePlan => BillingCycleEnum[basePlan.billing_cycle] >= BillingCycleEnum[currentPrimaryPlanBillingCycle]);
        // Find the last billing cycle
        const theLastSameBillingCycleBasePlanType = SameBillingCycleBasePlans.get(-1).type;
        // is this plan now
        return theLastSameBillingCycleBasePlanType === type
      }

      return false;
    },
  },
  methods: {
    ...mapMutations(['setApmInfo']),
    ...mapActions(['checkProMonthlyAndYearlyPriceChange']),
    _dispatch(type, ...args) {
      const planType = get(args, '0.type', '');
      let key = this.$options.key;
      if(planType && planTypes[planType]) {
        key = planTypes[planType];
      }
      this.$emit(type, key, ...args);
    },
    mx_main_title(title) {
      return <h1 class="opc-cart-item__title text-capitalize __PLACEHOLDER_first_title__">{ title }</h1>
    },
    mx_title(title, price) {
      return (
        <h3 class="opc-cart-item__title text-capitalize __PLACEHOLDER_first_title__">
          {title}
          {price !== undefined && <span class="opc-cart-item__value text-no-capitalize">{this.$money(price)}</span>}
        </h3>
      );
    },
    mx_price(plan) {
      const boughtPlan = this.boughtPlans.find((bp) => bp.billing_cycle === plan.billing_cycle && bp.plan_code === plan.plan_code);
      if (boughtPlan?.ratePlans?.length > 1) {
        return getMultipleRatePlanSumPrice(boughtPlan, plan.cartInfo.selected_number);
      }

      return plan.cartInfo.selected_number * plan.amount;
    },
    mx_price_bought(plan){
      const boughtPlan = this.boughtPlans.find((bp) => bp.billing_cycle === plan.billing_cycle && bp.plan_code === plan.plan_code);
      return this.mx_price(boughtPlan);
    },
    mx_price_delta(plan){
      const boughtPlan = this.boughtPlans.find((bp) => bp.billing_cycle === plan.billing_cycle && (bp.plan_code === plan.plan_code || bp.plan_code == PLAN_PRICE_CHANGE_UTILS.getOtherPlanCodeForPriceChange(plan.plan_code)));
      return this.mx_price(plan) - this.mx_price(boughtPlan);
    },
    mx_price$(plan){
      if(this.mode === CART_DISPLAY_MODE.DELTA) return this.mx_price_delta(plan);
      if(this.mode === CART_DISPLAY_MODE.BOUGHT) return this.mx_price_bought(plan);
      return this.mx_price(plan);
    },
    mx_subTitle(title, price) {
      return (
        <h2 class="opc-cart-item__sub-title __PLACEHOLDER_sub_title__">
          {title}
          {price !== undefined && <span class="opc-cart-item__value text-no-capitalize">{this.$money(price)}</span>}
        </h2>
      );
    },
    // FREE TRIAL - shopping cart title - show Title + 'Free Trial' as free trial plan = paid plan + 0 price
    mx_freeTrialTitle(title) {
      return (
        <h3 class="opc-cart-item__title text-capitalize __PLACEHOLDER_first_title__">
          {this.$t('billing.trial.title', [title])}
          <span class="opc-cart-item__value text-no-capitalize">{this.$money(0)}</span>
        </h3>
      );
    },
    mx_freeTrialStartDate(startTime = new Date()) {
      // show pst data time
      return this.$t('billing.trial.start_date', [dateTimeFormatWithUtcOffset(startTime, {offset: -8})]);
    },
    mx_freeTrialDesc(trialDays = DEFAULT_FREE_TRIAL_DAYS) {
      return (
        <div class="opc-cart-item__text">
          <p class="opc-cart-item__duration-text">{this.$t('billing.trial.duration_desc_cart', [trialDays])}</p>
          <p>{this.mx_freeTrialStartDate()}</p>
        </div>
      );
    },
    mx_license(num, unit = this.$t('billing.planform.unit.user')) {
      return (
        <div class="opc-cart-item__text">
          {num} {unit}
        </div>
      );
    },
    mx_license_bought(plan) {
      const boughtPlan = this.boughtPlans.find((bp) => bp.billing_cycle === plan.billing_cycle && (bp.plan_code === plan.plan_code || bp.plan_code == PLAN_PRICE_CHANGE_UTILS.getOtherPlanCodeForPriceChange(plan.plan_code)));
      return boughtPlan?.cartInfo?.selected_number;
    },
    mx_license_delta(plan) {
      const boughtPlan = this.boughtPlans.find((bp) => bp.billing_cycle === plan.billing_cycle && (bp.plan_code === plan.plan_code || bp.plan_code == PLAN_PRICE_CHANGE_UTILS.getOtherPlanCodeForPriceChange(plan.plan_code)));
      return plan.cartInfo.selected_number - boughtPlan?.cartInfo?.selected_number;
    },
    mx_license$(plan){
      let num;
      if(this.mode === CART_DISPLAY_MODE.DELTA) {
        num = this.mx_license_delta(plan);
      }else if(this.mode === CART_DISPLAY_MODE.BOUGHT){
        num = this.mx_license_bought(plan);
      }else{
        num = plan?.cartInfo?.selected_number;
      };
      return this.mx_license(num);
    },
    mx_cycle(cycle) {
      const labels = {
        [BILLING_CYCLE.MONTHLY]: this.$t('billing.order_summary.monthly_subscription'),
        [BILLING_CYCLE.ANNUAL]: this.$t('billing.order_summary.annual_subscription'),
        [BILLING_CYCLE.ONE_MONTH]: this.$t('billing.billing_cycle.one_month'),
        [BILLING_CYCLE.QUARTERLY]: this.$t('billing.order_summary.quarterly_subscription'),
        [BILLING_CYCLE.TWO_YEARS]: this.$t('billing.order_summary.two_years_subscription'),
        [BILLING_CYCLE.THREE_YEARS]: this.$t('billing.order_summary.three_years_subscription'),
        [BILLING_CYCLE.PAY_AS_YOU_GO]: this.$t('billing.pay_you_go'),
        [BILLING_CYCLE.PAY_PER_ATTENDEE]: this.$t('billing.billing_cycle.pay_per_attendee'),
      };
      if (!labels[cycle]) return '';
      return <div class="opc-cart-item__text text-capitalize"> {labels[cycle]}</div>;
    },
    mx_discount(planCode) {
      const { discountPlans } = this.discountWithAmountMap;
      const discount = discountPlans?.[planCode]?.discountAmount ?? 0;
      const expireDate = discountPlans?.[planCode]?.expireDate ?? "";

      if (!discount) return;

      let cartDiscountLabel = "";

      // partner with exclusive cart discount label
      if (!isEmptyVal(this.partnerRefer) && this.eligiblePartnerReferOffer) {
        const messageBody = getMessageTemplateByRules(this.partnerRefer?.cartDiscountLabel)?.messageBody;
        messageBody && (cartDiscountLabel = messageBody);
      }

      if (!cartDiscountLabel) {
        const expiresDesc = expireDate ?
          ` (${this.$t('billing.coupon.expiresDesc', [dateTimeFormat(expireDate)])})` : '';

        cartDiscountLabel = `${this.$t('billing.coupon.discount')} ${expiresDesc}`;
      }

      return (
        <div class="opc-cart-item__discount __PLACEHOLDER_discount_title__">
          <span>{cartDiscountLabel}</span>
          <span class="opc-cart-item__value text-no-capitalize">{this.$money(discount)}</span>
        </div>
      );
    },
    mx_edit(plan) {
      if (!this.$context.is.checkoutPage) return;
      return (
        <ZmButton
          type="link"
          aria-label={`${this.$t('billing.common.edit')} ${this.$t(plan?.planNameKey)}`}
          onClick={() => {
            const query = { region: plan?.region }
            // PRO INCREASE - Check Pro Monthly & Yearly Price change before redirect to config plan page
            // Fix the special case: Current sold to is US, then edit to Vietnam without saving, then click edit button in shopping-cart
            this.checkProMonthlyAndYearlyPriceChange()
            this.$router.push({
              path: routePathMap.config,
              query,
            });
            if (this.apmInfo.isApm) {
              this.$nextTick(() => {
                this.setApmInfo(getDefaultPayApmInfo())
            });

            }
            this.$tracking.orderSummary(
              `edit ${this.$pkg.components?.addon ? 'add-on product' : 'product'} - ${this.$t(plan.planNameKey)}`,
            );
          }}
        >
          {this.$t('billing.common.edit')}
        </ZmButton>
      );
    },
    mx_remove(plan) {
      if (!this.$context.is.checkoutPage) return;
      const configType = pageConfig?.editConfig?.planType;
      if(configType === plan.type && plansof(this.getSelectedPlans).type(plan.type).has(1)) return;
      return (
        <ZmButton
          type="link"
          aria-label={`${this.$t('billing.common.remove')} ${this.$t(plan.planNameKey)}`}
          onClick={() => {
            this._dispatch('remove', plan);
            this.$tracking.orderSummary(
              `remove ${this.$pkg.components.addon ? 'add-on product' : 'product'} - ${this.$t(plan.planNameKey)}`,
            );
          }}
        >
          {this.$t('billing.common.remove')}
        </ZmButton>
      );
    },
  },
  render() {
    const fn = renderKeys.find((x) => this.$props[x]);
    if (!fn) return null;
    if (!this[`$${fn}`]) return;
    return <div>{this[`$${fn}`]()}</div>;
  },
};
</script>

<style lang="scss">
@import '@/styles/responsive.scss';

.opc-cart-item {
  margin-bottom: 24px;
  @include media(mobile, tablet) {
    margin-bottom: 16px;
  }
  &__value {
    margin-left: auto;
    font-size: 16px;
    font-weight: 500;
    @include media(mobile, tablet) {
      white-space: nowrap;
      font-size: 14px;
      font-weight: 400;
      line-height: 1.5;
    }
  }
  &__title {
    text-align: left;
    font-weight: 700;
    font-size: 18px;
    line-height: 28px;
    color: #00053d;
    display: flex;
    @include media(mobile, tablet) {
      font-size: 14px;
      line-height: 1.5;
    }
  }
  &__sub-title {
    text-align: left;
    font-weight: 700;
    font-size: 14px;
    line-height: 22px;
    color: #00053d;
    display: flex;
    @include media(mobile, tablet) {
      font-weight: 500;
      line-height: 1.5;
    }
  }
  &__text {
    font-weight: 500;
    font-size: 14px;
    line-height: 22px;
    color: #515079;
    @include media(mobile, tablet) {
      font-weight: 400;
      line-height: 1.5;
    }
  }
  &__duration-text {
    color: #457857;
  }
  &__discount {
    display: flex;
    color: #4a7d5c;
    font-weight: 450;
    font-size: 16px;
    line-height: 24px;
    @include media(mobile, tablet) {
      font-size: 14px;
      font-weight: 400;
      line-height: 1.5;
    }
  }
  &__discount &__value::before {
    content: '-';
  }
}
</style>
